/**
 * PDF parser for Australian bank statements.
 * Uses pdfjs-dist to extract text, then parses transaction lines.
 */
import * as pdfjsLib from 'pdfjs-dist';

// Use the bundled worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url
).toString();

function parseDate(str) {
  if (!str) return null;
  const s = str.trim();
  const dmy = s.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{2,4})$/);
  if (dmy) {
    let [, d, m, y] = dmy;
    if (y.length === 2) y = (parseInt(y) > 50 ? '19' : '20') + y;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  const monthNames = { jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06', jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12' };
  const dMonY = s.match(/^(\d{1,2})\s+(\w{3})\s+(\d{2,4})$/);
  if (dMonY) {
    let [, d, mon, y] = dMonY;
    const m = monthNames[mon.toLowerCase()];
    if (!m) return null;
    if (y.length === 2) y = (parseInt(y) > 50 ? '19' : '20') + y;
    return `${y}-${m}-${d.padStart(2, '0')}`;
  }
  return null;
}

function parseAmount(str) {
  if (!str) return null;
  const cleaned = str.replace(/[$,\s]/g, '').replace(/[()]/g, m => m === '(' ? '-' : '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

async function extractText(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pages = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const lines = [];
    let currentLine = '';
    let lastY = null;
    for (const item of content.items) {
      if (lastY !== null && Math.abs(item.transform[5] - lastY) > 2) {
        if (currentLine.trim()) lines.push(currentLine.trim());
        currentLine = '';
      }
      currentLine += (currentLine ? ' ' : '') + item.str;
      lastY = item.transform[5];
    }
    if (currentLine.trim()) lines.push(currentLine.trim());
    pages.push(lines);
  }
  return pages;
}

// Date pattern: DD/MM/YYYY, DD-MM-YYYY, DD Mon YYYY
const DATE_RE = /^(\d{1,2}[/\-]\d{1,2}[/\-]\d{2,4}|\d{1,2}\s+\w{3}\s+\d{2,4})/;

// Dollar amount pattern (matches -$1,234.56 or $1,234.56)
const DOLLAR_AMT = /-?\$[\d,]+\.\d{2}/g;

// Two plain amounts at end of line (transaction amount + balance, no $ signs)
const TWO_PLAIN_AMT = /(-?[\d,]+\.\d{2})\s+(-?[\d,]+\.\d{2})\s*$/;

// Two plain amounts at START of rest (after date), followed by description
const TWO_PLAIN_AMT_LEADING = /^(-?[\d,]+\.\d{2})\s+(-?[\d,]+\.\d{2})\s+(.+)$/;

// Plain amount pattern (matches -1,234.56 or 1,234.56, with optional CR suffix)
const PLAIN_AMT = /(-?[\d,]+\.\d{2})\s*([Cc][Rr])?\s*$/;

function parseTransactionLine(line) {
  // Step 1: extract date from start
  const dateMatch = line.match(DATE_RE);
  if (!dateMatch) return null;
  const date = parseDate(dateMatch[1]);
  if (!date) return null;

  const rest = line.slice(dateMatch[0].length).trim();

  // Step 2: check for leading plain amounts (ING Orange Everyday: "DATE AMT BAL DESCRIPTION")
  const leadingMatch = rest.match(TWO_PLAIN_AMT_LEADING);
  if (leadingMatch) {
    const rawAmt = parseAmount(leadingMatch[1]);
    const balance = parseAmount(leadingMatch[2]);
    const rawDescription = leadingMatch[3].trim();
    if (rawAmt !== null && rawDescription) {
      const type = rawAmt < 0 ? 'expense' : 'income';
      return { date, rawDescription, amount: Math.abs(rawAmt), type, balance };
    }
  }

  // Step 3: find all $-prefixed amounts in the line
  const dollarAmounts = [...rest.matchAll(DOLLAR_AMT)];

  if (dollarAmounts.length >= 2) {
    // ING format: "description -$4.00 $244.85" (transaction amount + balance)
    const txAmtMatch = dollarAmounts[dollarAmounts.length - 2];
    const balAmtMatch = dollarAmounts[dollarAmounts.length - 1];
    const rawDescription = rest.slice(0, txAmtMatch.index).trim();
    const rawAmt = parseAmount(txAmtMatch[0]);
    const balance = parseAmount(balAmtMatch[0]);
    if (rawAmt === null || !rawDescription) return null;
    const type = rawAmt < 0 ? 'expense' : 'income';
    return { date, rawDescription, amount: Math.abs(rawAmt), type, balance };
  }

  if (dollarAmounts.length === 1) {
    // Single $amount: "description -$4.00" (no balance column)
    const txAmtMatch = dollarAmounts[0];
    const rawDescription = rest.slice(0, txAmtMatch.index).trim();
    const rawAmt = parseAmount(txAmtMatch[0]);
    if (rawAmt === null || !rawDescription) return null;
    const type = rawAmt < 0 ? 'expense' : 'income';
    return { date, rawDescription, amount: Math.abs(rawAmt), type, balance: null };
  }

  // Step 3: no $ signs -- try two plain amounts at end (amount + balance)
  const twoPlainMatch = rest.match(TWO_PLAIN_AMT);
  if (twoPlainMatch) {
    const rawDescription = rest.slice(0, twoPlainMatch.index).trim();
    const rawAmt = parseAmount(twoPlainMatch[1]);
    const balance = parseAmount(twoPlainMatch[2]);
    if (rawAmt === null || !rawDescription) return null;
    const type = rawAmt < 0 ? 'expense' : 'income';
    return { date, rawDescription, amount: Math.abs(rawAmt), type, balance };
  }

  // Step 4: single plain amount at end (with optional CR suffix)
  const plainMatch = rest.match(PLAIN_AMT);
  if (plainMatch) {
    const rawDescription = rest.slice(0, plainMatch.index).trim();
    const rawAmt = parseAmount(plainMatch[1]);
    if (rawAmt === null || !rawDescription) return null;
    const isCr = plainMatch[2] && plainMatch[2].toLowerCase() === 'cr';
    const type = isCr ? 'income' : 'expense';
    return { date, rawDescription, amount: Math.abs(rawAmt), type, balance: null };
  }

  return null;
}

export async function parsePDF(file) {
  const warnings = [];
  let allLines;
  try {
    const pages = await extractText(file);
    allLines = pages.flat();
  } catch (err) {
    return { rows: [], warnings: ['Failed to read PDF: ' + err.message] };
  }

  if (allLines.length === 0) {
    return { rows: [], warnings: ['No text found in PDF. The file may be image-based (scanned).'] };
  }

  const rows = [];
  for (const line of allLines) {
    const tx = parseTransactionLine(line);
    if (tx) {
      rows.push(tx);
    } else if (rows.length > 0 && !DATE_RE.test(line)) {
      // Continuation line: append to previous transaction description
      const prev = rows[rows.length - 1];
      const trimmed = line.trim();
      if (trimmed && !/^(total|opening|closing|balance|page\s)/i.test(trimmed)) {
        prev.rawDescription += ' ' + trimmed;
      }
    }
  }

  if (rows.length === 0) {
    warnings.push('No transactions found in PDF. The format may not be supported yet.');
  }

  return { rows, warnings, needsManualMapping: false };
}
